// Console / Edit Page -- the ONE official serializer/deserializer between
// the Builder's canonical `contentBlocks` (+ customCss/customJs) and a
// complete HTML/CSS/JS document. Used by HowWeDoItCodeEditorModal.jsx's
// Full Page/HTML/CSS/JS tabs -- never duplicated elsewhere.
//
// Design (see project notes for the full rationale):
//  - The visible <body> markup is rendered by the SAME shared renderer used
//    everywhere else (BlockList, from renderers/BlockList.jsx) via
//    ReactDOMServer.renderToStaticMarkup -- editor preview, public view and
//    this serializer can never structurally diverge.
//  - A `<script type="application/json" id="elakay-page-metadata">` carries
//    a lossless contentBlocks snapshot. On the way back in it is merged by
//    blockId with the current MongoDB-backed state, which remains the base
//    for database-owned `_id` values and for blocks absent from the HTML.
//  - The visible markup is still READ for two bounded, well-defined things:
//    (1) block ORDER and editable fields, via the stable
//        data-elakay-block-id markers BlockList's renderBlock() already
//        stamps on every block wrapper; omitting markup never implicitly
//        deletes the corresponding database block;
//    (2) explicitly marked fields for every persisted block type currently
//        present in the collection (text/title/image/video/button/code).
//    Structural additions and deletions still go through the JSON tab or
//    the visual Page Builder; HTML is deliberately a bounded adapter rather
//    than a guess-based universal parser.
//  - A document with no trustworthy metadata (missing, or present but
//    malformed) -- typically a brand new page pasted in from elsewhere, or
//    written by hand -- is NOT rejected: the whole <body> becomes one
//    editable "text" block instead of hard-blocking Save/Apply. See
//    buildFallbackBlocksFromBody().
import ReactDOMServer from "react-dom/server";
import React from "react";
import BlockList from "./renderers/BlockList";
import { generateBlockId } from "./blockRegistry";
import { CodeDocumentError, normalizeContentBlocks } from "./howWeDoItCodeDocument";
import { canonicalizeHowWeDoItMediaUrl } from "./howWeDoItMediaUrl";
import { assessTextBlockFidelity } from "./howWeDoItContentFormats";
import { elakayAutoText } from "../dataElakay";

export const PAGE_METADATA_SCRIPT_ID = "elakay-page-metadata";

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeAttr = (value = "") => escapeHtml(value).replace(/"/g, "&quot;");

// Safe, narrow "pretty print": only ever inserts a newline exactly at a
// `><div id="hwdi-block-` boundary -- a position that, by construction of
// BlockList's own renderBlock(), NEVER sits inside authored text (block
// wrapper siblings are rendered back-to-back with no text node between
// them, at any nesting depth). Never touches any other `><` boundary, so it
// can never introduce a rendering-visible space into inline author content.
const prettyPrintBlocksMarkup = (markup) => markup.replace(/><div id="hwdi-block-/g, '>\n<div id="hwdi-block-');

/**
 * The one official Builder-document -> complete HTML document serializer.
 * @param {{contentBlocks?: object[], customCss?: string, customJs?: string, title?: string, language?: string}} tutorial
 * @returns {string} a full `<!doctype html>...</html>` document string.
 */
export const serializeHowWeDoItPage = (tutorial = {}) => {
  const contentBlocks = Array.isArray(tutorial.contentBlocks) ? tutorial.contentBlocks : [];
  const css = typeof tutorial.customCss === "string" ? tutorial.customCss : "";
  const js = typeof tutorial.customJs === "string" ? tutorial.customJs : "";
  const title = escapeHtml(tutorial.title || "How We Do It");
  const lang = escapeAttr(tutorial.language || "fr");

  const bodyMarkup = prettyPrintBlocksMarkup(
    ReactDOMServer.renderToStaticMarkup(React.createElement(BlockList, { blocks: contentBlocks }))
  );

  // Escaping "<" prevents an authored string containing "</script>" from
  // terminating this inert JSON script element early. JSON.parse restores the
  // original characters when the document comes back through the Console.
  const metadataJson = JSON.stringify({ version: 1, contentBlocks }, null, 2).replace(/</g, "\\u003c");

  return `<!doctype html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title}</title>
  <style>
${css}
  </style>
</head>
<body>
  <main data-elakay-how-we-do-it-root="1">
${bodyMarkup}
  </main>

  <script type="application/json" id="${PAGE_METADATA_SCRIPT_ID}">
${metadataJson}
  </script>

  <script>
${js}
  </script>
</body>
</html>
`;
};

// The independent freeform-HTML page mode's ONE document builder --
// htmlCode + customCss + customJs -> a complete HTML document, mirroring
// serializeHowWeDoItPage() above but never touching contentBlocks at all.
// Used by HowWeDoItSandboxedFrame whenever hasTutorialHtmlCode(tutorial) is
// true (howWeDoItRenderMode.js). Handles both authoring styles:
//   - a body FRAGMENT ("<section>...</section>") -- wrapped in a full
//     document, customCss/customJs inserted normally;
//   - a COMPLETE document (contains a top-level <html> tag) -- customCss/
//     customJs are spliced into the author's own <head>/<body> instead of
//     being wrapped again, with fallbacks when <head>/<body> are missing so
//     a hand-written partial document never silently drops the tutorial's
//     CSS/JS.
export const buildTutorialHtmlDocument = ({ htmlCode = "", customCss = "", customJs = "", title = "How We Do It", language = "fr" } = {}) => {
  const code = typeof htmlCode === "string" ? htmlCode : "";
  const css = typeof customCss === "string" ? customCss : "";
  const js = typeof customJs === "string" ? customJs : "";
  const isFullDocument = /<html[\s>]/i.test(code);

  if (!isFullDocument) {
    const fragmentCss = css.trim() && code.includes(css.trim()) ? "" : css;
    const fragmentJs = js.trim() && code.includes(js.trim()) ? "" : js;
    return `<!doctype html>
<html lang="${escapeAttr(language)}">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${escapeHtml(title)}</title>
<style>
${fragmentCss}
</style>
</head>
<body>
${code}
<script>
${fragmentJs}
</script>
</body>
</html>
`;
  }

  let doc = code;
  // A page exported from the Console (or pasted back after an export)
  // already carries the tutorial's CSS/JS inline: never inject them a second
  // time (the script would otherwise run twice).
  const styleBlock = css.trim() && !doc.includes(css.trim()) ? `<style data-elakay-custom-css>\n${css}\n</style>\n` : "";
  const scriptBlock = js.trim() && !doc.includes(js.trim()) ? `<script data-elakay-custom-js>\n${js}\n</script>\n` : "";

  if (/<\/head>/i.test(doc)) {
    doc = doc.replace(/<\/head>/i, `${styleBlock}</head>`);
  } else if (/<head[^>]*>/i.test(doc)) {
    doc = doc.replace(/(<head[^>]*>)/i, `$1\n${styleBlock}`);
  } else if (/<html[^>]*>/i.test(doc)) {
    // No <head> at all -- add a minimal one right after <html ...>.
    doc = doc.replace(/(<html[^>]*>)/i, `$1\n<head>\n${styleBlock}</head>`);
  } else {
    doc = `${styleBlock}${doc}`;
  }

  if (/<\/body>/i.test(doc)) {
    doc = doc.replace(/<\/body>/i, `${scriptBlock}</body>`);
  } else {
    // No </body> to anchor on -- append at the very end rather than drop it.
    doc = `${doc}\n${scriptBlock}`;
  }

  return doc;
};

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

const flattenBlocks = (blocks = []) =>
  blocks.flatMap((block) => [block, ...flattenBlocks(Array.isArray(block.children) ? block.children : [])]);

const mergeBlock = (existing, incoming) => {
  if (!existing) return incoming;
  if (existing.type !== incoming.type) {
    throw new CodeDocumentError(
      `Block "${incoming.blockId}" changed type from "${existing.type}" to "${incoming.type}". ` +
        "Create a new block from the Builder or JSON tab instead of changing an existing block's identity."
    );
  }

  const merged = {
    ...existing,
    ...incoming,
    content: { ...(existing.content || {}), ...(incoming.content || {}) },
    settings: { ...(existing.settings || {}), ...(incoming.settings || {}) },
  };
  if (existing._id !== undefined) merged._id = existing._id;
  if (Object.hasOwn(existing, "children") || Object.hasOwn(incoming, "children")) {
    merged.children = mergeBlockArrays(existing.children || [], incoming.children || []);
  } else {
    delete merged.children;
  }
  return merged;
};

// Metadata is allowed to update/reorder blocks, but never deletes a MongoDB
// block merely because that block is absent from the HTML/metadata being
// edited. Existing blocks form the base; new, valid metadata blocks are
// appended/merged. Deletion remains an explicit Builder action.
const mergeBlockArrays = (existingBlocks = [], incomingBlocks = []) => {
  const existingById = new Map(existingBlocks.map((block) => [String(block.blockId), block]));
  const incomingIds = new Set(incomingBlocks.map((block) => String(block.blockId)));
  const merged = incomingBlocks.map((block) => mergeBlock(existingById.get(String(block.blockId)), block));
  existingBlocks.forEach((block) => {
    if (!incomingIds.has(String(block.blockId))) merged.push(block);
  });
  return merged.map((block, index) => ({ ...block, order: index }));
};

const parseHtmlDocument = (source) => {
  if (typeof DOMParser === "undefined") {
    throw new CodeDocumentError("HTML parsing is unavailable in this environment.");
  }
  return new DOMParser().parseFromString(String(source || ""), "text/html");
};

/**
 * Reads #elakay-page-metadata and merges it onto the current MongoDB-derived
 * block tree. It never replaces the existing tree blindly and never trusts a
 * metadata `_id` over the current block with the same stable blockId.
 */
export const parseElakayPageMetadata = (fullHtml, existingContentBlocks = []) => {
  const doc = parseHtmlDocument(fullHtml);
  const existing = normalizeContentBlocks(Array.isArray(existingContentBlocks) ? existingContentBlocks : []);
  const metadataScript = doc.getElementById(PAGE_METADATA_SCRIPT_ID);
  if (!metadataScript) {
    return { status: "missing", metadata: null, contentBlocks: existing, document: doc };
  }

  try {
    const parsed = JSON.parse(metadataScript.textContent);
    if (!isPlainObject(parsed) || !Array.isArray(parsed.contentBlocks)) {
      throw new CodeDocumentError("Page metadata must contain a contentBlocks array.");
    }
    const incoming = normalizeContentBlocks(parsed.contentBlocks);
    const contentBlocks = mergeBlockArrays(existing, incoming);
    return {
      status: "valid",
      metadata: { ...parsed, contentBlocks },
      contentBlocks,
      document: doc,
    };
  } catch (error) {
    return { status: "invalid", metadata: null, contentBlocks: existing, document: doc, error };
  }
};

const findBlockElement = (doc, blockId) =>
  [...doc.querySelectorAll("[data-elakay-block-id]")].find(
    (element) => element.getAttribute("data-elakay-block-id") === String(blockId)
  ) || null;

// A parent's wrapper may contain marked descendants. Only read a field owned
// by THIS wrapper, never a child's field with the same name.
const findOwnedField = (wrapper, field) =>
  (wrapper.matches(`[data-elakay-field="${field}"]`) ? wrapper : null) ||
  [...wrapper.querySelectorAll(`[data-elakay-field="${field}"]`)].find(
    (element) => element.closest("[data-elakay-block-id]") === wrapper
  ) || null;

const readAuthoredMediaUrl = (element) => {
  if (!element) return null;
  const rendered = element.getAttribute("data-elakay-rendered-url");
  const source = element.getAttribute("data-elakay-source-url");
  const current = element.getAttribute("src");
  // Changing src explicitly in Console wins. If src still equals the rendered
  // display URL, keep the canonical source path carried beside it.
  const value = rendered !== null && current !== rendered ? current : source !== null ? source : current;
  return value === null ? null : canonicalizeHowWeDoItMediaUrl(value);
};

const textAdapter = {
  fromDom(wrapper, block) {
    const field = findOwnedField(wrapper, "html");
    return field ? { ...block, content: { ...block.content, html: field.innerHTML } } : block;
  },
};

const titleAdapter = {
  fromDom(wrapper, block) {
    const field = findOwnedField(wrapper, "text");
    if (!field) return block;
    const level = /^H([1-3])$/.exec(field.tagName)?.[1];
    return {
      ...block,
      content: { ...block.content, text: field.textContent },
      settings: level ? { ...block.settings, level: Number(level) } : block.settings,
    };
  },
};

const imageAdapter = {
  fromDom(wrapper, block) {
    const image = findOwnedField(wrapper, "url");
    if (!image) return block;
    const nextContent = { ...block.content };
    const url = readAuthoredMediaUrl(image);
    if (url !== null) nextContent.url = url;
    if (Object.hasOwn(nextContent, "alt")) nextContent.alt = image.getAttribute("alt") || "";
    if (Object.hasOwn(nextContent, "title")) nextContent.title = image.getAttribute("title") || "";
    const caption = findOwnedField(wrapper, "caption");
    if (caption && Object.hasOwn(nextContent, "caption")) nextContent.caption = caption.textContent;
    const link = findOwnedField(wrapper, "image-link");
    const nextSettings = { ...block.settings };
    if (link && Object.hasOwn(nextSettings, "linkUrl")) nextSettings.linkUrl = link.getAttribute("href") || "";
    return { ...block, content: nextContent, settings: nextSettings };
  },
};

const videoAdapter = {
  fromDom(wrapper, block) {
    const media = findOwnedField(wrapper, "url");
    const nextContent = { ...block.content };
    const nextSettings = { ...block.settings };
    if (media) {
      const url = readAuthoredMediaUrl(media);
      if (url !== null) nextContent.url = url;
      const source = media.getAttribute("data-elakay-video-source") || wrapper.getAttribute("data-elakay-video-source");
      if (source && Object.hasOwn(nextContent, "source")) nextContent.source = source;
      if (media.tagName === "VIDEO" && Object.hasOwn(nextContent, "thumbnail")) {
        nextContent.thumbnail = canonicalizeHowWeDoItMediaUrl(media.getAttribute("poster") || "");
      }
    }
    const title = findOwnedField(wrapper, "title");
    const description = findOwnedField(wrapper, "description");
    if (title && Object.hasOwn(nextContent, "title")) nextContent.title = title.textContent;
    if (description && Object.hasOwn(nextContent, "description")) nextContent.description = description.textContent;
    return { ...block, content: nextContent, settings: nextSettings };
  },
};

const buttonAdapter = {
  fromDom(wrapper, block) {
    const button = findOwnedField(wrapper, "button");
    if (!button) return block;
    return {
      ...block,
      content: {
        ...block.content,
        label: button.textContent,
        url: button.getAttribute("href") || "",
      },
    };
  },
};

const codeAdapter = {
  fromDom(wrapper, block) {
    const code = findOwnedField(wrapper, "code");
    if (!code) return block;
    const language = code.getAttribute("data-elakay-language") ||
      [...code.classList].find((name) => name.startsWith("language-"))?.slice("language-".length);
    return {
      ...block,
      content: {
        ...block.content,
        code: code.textContent,
        ...(language ? { language } : {}),
      },
    };
  },
};

// Only the block types found in the real howwedoittutorials audit are given
// content-field adapters. `subtitle` reuses the already-established title
// marker for backward compatibility with the frontend's pre-existing type.
export const blockAdapters = {
  text: textAdapter,
  title: titleAdapter,
  subtitle: titleAdapter,
  image: imageAdapter,
  video: videoAdapter,
  button: buttonAdapter,
  code: codeAdapter,
};

const reorderPreservingAbsent = (blocks, htmlIds) => {
  const present = new Set(htmlIds);
  const byId = new Map(blocks.map((block) => [String(block.blockId), block]));
  let cursor = 0;
  return blocks.map((block) => {
    if (!present.has(String(block.blockId))) return block;
    const replacement = byId.get(htmlIds[cursor]);
    cursor += 1;
    return replacement || block;
  }).map((block, index) => ({ ...block, order: index }));
};

const directChildIds = (wrapper) =>
  [...wrapper.querySelectorAll("[data-elakay-block-id]")]
    .filter((element) => element.parentElement?.closest("[data-elakay-block-id]") === wrapper)
    .map((element) => element.getAttribute("data-elakay-block-id"));

const reconcileOrder = (blocks, doc, parentWrapper = null, isChildList = false) => {
  let htmlIds = [];
  if (!parentWrapper) {
    const list = doc.body.querySelector(".hwdi-block-list");
    if (list) {
      htmlIds = [...list.children]
        .map((element) => element.getAttribute("data-elakay-block-id"))
        .filter(Boolean);
    }
  } else {
    htmlIds = directChildIds(parentWrapper);
  }

  const known = new Set(blocks.map((block) => String(block.blockId)));
  const recognized = htmlIds.filter((id) => known.has(String(id)));
  const ordered = recognized.length ? reorderPreservingAbsent(blocks, recognized) : blocks;
  return ordered.map((block) => {
    const wrapper = findBlockElement(doc, block.blockId);
    if (isChildList || !Object.hasOwn(block, "children")) return block;
    return { ...block, children: wrapper ? reconcileOrder(block.children || [], doc, wrapper, true) : block.children || [] };
  });
};

const applyDomFields = (blocks, doc, isChildList = false) =>
  blocks.map((block) => {
    const wrapper = findBlockElement(doc, block.blockId);
    let next = block;
    if (wrapper) {
      const htmlType = wrapper.getAttribute("data-elakay-block-type");
      if (htmlType && htmlType !== block.type) {
        throw new CodeDocumentError(
          `Block "${block.blockId}" has type "${htmlType}" in HTML but "${block.type}" in the document.`
        );
      }
      const adapter = blockAdapters[block.type];
      if (adapter) next = adapter.fromDom(wrapper, block);
    }
    if (isChildList || !Object.hasOwn(next, "children")) return next;
    return { ...next, children: applyDomFields(next.children || [], doc, true) };
  });

/**
 * Non-destructively synchronizes marked HTML fields onto existing blocks.
 * Missing HTML markers never delete blocks; unknown/duplicate ids fail fast.
 */
export const syncContentBlocksFromHtml = ({ html, existingContentBlocks = [], metadataContentBlocks = null }) => {
  const doc = parseHtmlDocument(html);
  const existing = normalizeContentBlocks(Array.isArray(existingContentBlocks) ? existingContentBlocks : []);
  const metadata = Array.isArray(metadataContentBlocks) ? normalizeContentBlocks(metadataContentBlocks) : [];
  let nextBlocks = metadata.length ? mergeBlockArrays(existing, metadata) : existing;

  const elements = [...doc.querySelectorAll("[data-elakay-block-id]")];
  const htmlIds = elements.map((element) => element.getAttribute("data-elakay-block-id")).filter(Boolean);
  const seen = new Set();
  const duplicate = htmlIds.find((id) => (seen.has(id) ? true : (seen.add(id), false)));
  if (duplicate) throw new CodeDocumentError(`Duplicate block id in the HTML: "${duplicate}".`);

  const knownIds = new Set(flattenBlocks(nextBlocks).map((block) => String(block.blockId)));
  const unknown = htmlIds.filter((id) => !knownIds.has(String(id)));
  if (unknown.length) {
    throw new CodeDocumentError(
      `Unrecognized block id(s) in the HTML with no matching MongoDB/metadata block: ${unknown.join(", ")}. ` +
        "Add new blocks from the Page Builder or the JSON tab."
    );
  }

  nextBlocks = reconcileOrder(nextBlocks, doc);
  return applyDomFields(nextBlocks, doc);
};

export const auditHowWeDoItContentBlocks = ({ html, contentBlocks = [] }) => {
  const doc = parseHtmlDocument(html);
  const mongoBlocks = flattenBlocks(Array.isArray(contentBlocks) ? contentBlocks : []);
  const mongoIds = mongoBlocks.map((block) => String(block.blockId));
  const htmlIds = [...doc.querySelectorAll("[data-elakay-block-id]")]
    .map((element) => element.getAttribute("data-elakay-block-id"))
    .filter(Boolean);
  const metadataScript = doc.getElementById(PAGE_METADATA_SCRIPT_ID);
  let metadataIds = [];
  try {
    metadataIds = flattenBlocks(JSON.parse(metadataScript?.textContent || "{}").contentBlocks || [])
      .map((block) => String(block.blockId));
  } catch {
    metadataIds = [];
  }
  const mongoSet = new Set(mongoIds);
  const htmlSet = new Set(htmlIds);
  const metadataSet = new Set(metadataIds);
  const expectedVisibleIds = [];
  const collectVisible = (blocks, parentVisible = true) => (blocks || []).forEach((block) => {
    const visible = parentVisible && block.visible !== false;
    if (visible) expectedVisibleIds.push(String(block.blockId));
    collectVisible(block.children || [], visible);
  });
  collectVisible(contentBlocks);
  const duplicates = htmlIds.filter((id, index) => htmlIds.indexOf(id) !== index);
  const missingInHtml = mongoIds.filter((id) => !htmlSet.has(id));
  const missingVisibleInHtml = expectedVisibleIds.filter((id) => !htmlSet.has(id));
  const unknownHtmlBlocks = htmlIds.filter((id) => !mongoSet.has(id));
  const metadataMismatch = mongoIds.filter((id) => !metadataSet.has(id)).concat(metadataIds.filter((id) => !mongoSet.has(id)));
  return {
    mongoBlocks: mongoIds.length,
    htmlBlocks: htmlIds.length,
    matchedByBlockId: htmlIds.filter((id) => mongoSet.has(id)).length,
    missingInHtml,
    missingVisibleInHtml,
    unknownHtmlBlocks,
    duplicateHtmlBlockIds: [...new Set(duplicates)],
    metadataBlocks: metadataIds.length,
    metadataMismatch: [...new Set(metadataMismatch)],
    status: missingVisibleInHtml.length || unknownHtmlBlocks.length || duplicates.length || metadataMismatch.length
      ? "NOT_SYNCED"
      : "SYNCED",
  };
};

// Fallback for a document with no trustworthy elakay-page-metadata (missing,
// or present but unparseable/malformed) -- e.g. a brand new page pasted in
// from somewhere else, or hand-written from scratch, that never went
// through serializeHowWeDoItPage(). Rather than hard-block Save/Apply
// entirely (the actual bug this fixes: "I can't save/edit"), the whole
// <body> is kept as ONE rich-text block -- no per-element guessing, no
// fragile parser, but any valid pasted HTML document becomes immediately
// saveable and editable. Further structural editing (splitting it into
// multiple blocks, adding settings) then goes through the normal Page
// Builder/JSON tab, same documented boundary as everywhere else in this
// file.
const buildFallbackBlocksFromBody = (doc) => {
  // <style>/<script> are already extracted separately into customCss/
  // customJs below -- stripped here so they don't also sit inertly
  // duplicated inside the text block's own HTML.
  const bodyClone = doc.body.cloneNode(true);
  bodyClone.querySelectorAll("style, script").forEach((el) => el.remove());
  return [
    {
      blockId: generateBlockId(),
      type: "text",
      order: 0,
      visible: true,
      content: { html: bodyClone.innerHTML.trim() },
      settings: {},
      children: [],
    },
  ];
};

const isClassicScript = (el) => {
  const type = (el.getAttribute("type") || "").trim().toLowerCase();
  return !type || type === "text/javascript" || type === "application/javascript";
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

// Visible content of the body that sits OUTSIDE every block wrapper (the
// visual builder only reads block wrappers, so this content would be lost).
const contentOutsideBlocks = (doc) => {
  const clone = doc.body.cloneNode(true);
  clone.querySelectorAll("script, style, template, [data-elakay-block-id]").forEach((el) => el.remove());
  return normalizeText(clone.textContent).length > 0 || !!clone.querySelector("img, video, audio, iframe, svg, table, canvas, object, embed, picture, input, button, select, textarea");
};

// Blocks whose visible text in the pasted HTML differs from what the builder
// will render after the import: an edit the builder cannot represent (only
// marked fields are read back) and would silently drop.
const blocksWithUnsupportedEdits = (doc, contentBlocks) => {
  const rendered = parseHtmlDocument(
    `<!doctype html><html><body>${ReactDOMServer.renderToStaticMarkup(React.createElement(BlockList, { blocks: contentBlocks }))}</body></html>`
  );
  const types = new Map(flattenBlocks(contentBlocks).map((block) => [String(block.blockId), block.type]));
  return [...doc.querySelectorAll("[data-elakay-block-id]")]
    .map((element) => element.getAttribute("data-elakay-block-id"))
    .filter((id) => types.has(String(id)) && types.get(String(id)) !== "elakaySwiper")
    .filter((id) => {
      const before = findBlockElement(doc, id);
      const after = findBlockElement(rendered, id);
      return !after || normalizeText(before?.textContent) !== normalizeText(after.textContent);
    });
};

const lossError = (message, reasons = []) => {
  const error = new CodeDocumentError(message);
  error.recommendHtmlMode = true;
  error.lossReasons = reasons;
  return error;
};

/**
 * The one official complete HTML document -> Builder-document deserializer.
 * Throws CodeDocumentError with a human-readable message on anything it
 * cannot safely apply -- never silently drops data. When the visual builder
 * could not keep the pasted content (no Elakay block markers, external
 * resources, edits outside the marked fields...), the error carries
 * `recommendHtmlMode: true`: the Console then offers to keep the page,
 * byte for byte, as a "Page HTML" instead.
 * @param {string} source
 * @returns {{contentBlocks: object[], customCss: string, customJs: string}}
 */
export const deserializeHowWeDoItPage = (source, existingContentBlocks = []) => {
  if (typeof source !== "string" || !source.trim()) {
    throw new CodeDocumentError("The document is empty.");
  }
  // DOMParser's text/html mode always auto-repairs its result into a full
  // html/head/body tree, even from a bare fragment -- so checking the
  // PARSED doc can never actually catch a missing tag. Checked against the
  // raw source text instead, which is what the user actually typed.
  if (!/<html[\s>]/i.test(source) || !/<head[\s>]/i.test(source) || !/<body[\s>]/i.test(source)) {
    const error = new CodeDocumentError(
      elakayAutoText(
        "howWeDoIt_console_incompleteDocument",
        "Incomplete document -- expected a full <html>...<head>...<body>...</html> page. Pasting only a body fragment? Use the HTML tab instead of Full Page, or the \"Page HTML\" mode."
      )
    );
    error.recommendHtmlMode = true;
    throw error;
  }

  const parsedMetadata = parseElakayPageMetadata(source, existingContentBlocks);
  const doc = parsedMetadata.document;

  // Resources the builder cannot store (it keeps ONE inline CSS and ONE
  // inline script): refusing is better than dropping them.
  const unsupported = [];
  doc.querySelectorAll('link[rel~="stylesheet"]').forEach((link) => unsupported.push(`<link href="${link.getAttribute("href") || ""}">`));
  doc.querySelectorAll("script").forEach((el) => {
    if (el.id === PAGE_METADATA_SCRIPT_ID) return;
    if (el.getAttribute("src")) unsupported.push(`<script src="${el.getAttribute("src")}">`);
    else if (!isClassicScript(el) && (el.getAttribute("type") || "").toLowerCase() !== "application/json") unsupported.push(`<script type="${el.getAttribute("type")}">`);
    else if ((el.getAttribute("type") || "").toLowerCase() === "application/json") unsupported.push('<script type="application/json">');
  });
  doc.querySelectorAll("style[media]").forEach(() => unsupported.push("<style media>"));
  if (unsupported.length) {
    throw lossError(
      elakayAutoText("howWeDoIt_console_lossResources", "This page uses resources the visual blocks cannot keep. Use the \"Page HTML\" mode to keep it exactly as it is."),
      unsupported
    );
  }

  const hasExistingBlocks = parsedMetadata.contentBlocks.length > 0;
  const hasMarkers = doc.querySelector("[data-elakay-block-id]") !== null;
  let contentBlocks;
  if (hasExistingBlocks) {
    if (contentOutsideBlocks(doc)) {
      throw lossError(
        hasMarkers
          ? elakayAutoText("howWeDoIt_console_lossOutside", "Part of this page is outside the Elakay blocks and would be ignored by the visual builder. Use the \"Page HTML\" mode to keep everything.")
          : elakayAutoText("howWeDoIt_console_lossNoMarkers", "This page was not produced by the visual builder (no Elakay block markers): its content would be ignored. Use the \"Page HTML\" mode to keep it.")
      );
    }
    contentBlocks = syncContentBlocksFromHtml({ html: source, existingContentBlocks: parsedMetadata.contentBlocks });
    const changed = blocksWithUnsupportedEdits(doc, contentBlocks);
    if (changed.length) {
      throw lossError(
        elakayAutoText("howWeDoIt_console_lossEdits", "Some edits are outside the fields the visual builder can read back and would be lost. Edit them in the Page Builder, or use the \"Page HTML\" mode."),
        changed.map((id) => `block ${id}`)
      );
    }
  } else {
    const fidelity = assessTextBlockFidelity(doc.body);
    if (!fidelity.lossless) {
      throw lossError(
        elakayAutoText("howWeDoIt_console_lossTextBlock", "As a visual text block, this page would lose part of its markup. Use the \"Page HTML\" mode to keep it exactly as it is."),
        fidelity.reasons
      );
    }
    contentBlocks = buildFallbackBlocksFromBody(doc);
  }

  const styleEls = [...doc.querySelectorAll("style")];
  const customCss = styleEls.map((el) => el.textContent).join("\n\n").trim();

  const scriptEls = [...doc.querySelectorAll("script")].filter((el) => el.id !== PAGE_METADATA_SCRIPT_ID && isClassicScript(el));
  const customJs = scriptEls.map((el) => el.textContent).join("\n\n").trim();

  return { contentBlocks, customCss, customJs };
};

// ---------------------------------------------------------------------------
// Full Page <-> HTML/CSS/JS sub-tab lenses.
//
// These extract/replace ONE exact, known region of a `fullPageCode` string
// that always originated from serializeHowWeDoItPage() a moment earlier (in
// HowWeDoItCodeEditorModal.jsx). Unlike deserializeHowWeDoItPage() -- which
// must robustly parse arbitrary hand-edited HTML at Apply time -- these only
// ever operate on text we ourselves authored, so a plain, exact regex on our
// own literal template markers is safe here, not a fragile guess. If the
// user deletes one of those markers from the Full Page tab, the matching
// getter simply returns "" and the matching setter is a safe no-op (the
// string comes back unchanged) rather than throwing or corrupting data.
const HTML_SECTION_RE = /(<main data-elakay-how-we-do-it-root="1">\n)([\s\S]*?)(\n\s*<\/main>)/;
const CSS_SECTION_RE = /(<style>\n)([\s\S]*?)(\n\s*<\/style>)/;
// The customJs <script> is the plain, attribute-less one right before
// </body> -- the metadata script always carries type="application/json", so
// this can never match it by mistake.
const JS_SECTION_RE = /(<script>\n)([\s\S]*?)(\n\s*<\/script>\n<\/body>)/;

export const getFullPageHtmlSection = (fullPageCode = "") => HTML_SECTION_RE.exec(fullPageCode)?.[2] ?? "";
export const getFullPageCssSection = (fullPageCode = "") => CSS_SECTION_RE.exec(fullPageCode)?.[2] ?? "";
export const getFullPageJsSection = (fullPageCode = "") => JS_SECTION_RE.exec(fullPageCode)?.[2] ?? "";

const spliceSection = (fullPageCode, regex, next) =>
  regex.test(fullPageCode) ? fullPageCode.replace(regex, (_match, before, _old, after) => `${before}${next}${after}`) : fullPageCode;

export const setFullPageHtmlSection = (fullPageCode, html) => spliceSection(fullPageCode, HTML_SECTION_RE, html);
export const setFullPageCssSection = (fullPageCode, css) => spliceSection(fullPageCode, CSS_SECTION_RE, css);
export const setFullPageJsSection = (fullPageCode, js) => spliceSection(fullPageCode, JS_SECTION_RE, js);

// Retained as a safe utility for any caller that needs to display author CSS
// outside the document renderer. The official Preview/Detail renderer uses
// an isolated sandboxed frame for every tutorial.
export const buildScopedCustomCss = (css, scopeSelector) => {
  if (!css || !css.trim()) return "";
  return `@scope (${scopeSelector}) {\n${css}\n}`;
};
